import { useState } from 'react';
import axios from 'axios';

const BACKEND_URL = window.location.origin || process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper prompts for users
const PROMPT_SUGGESTIONS = [
  "Make it more vibrant and colorful",
  "Add a professional business atmosphere",
  "Create a warm and welcoming mood",
  "Make it look modern and futuristic",
  "Add dramatic lighting effects"
];

// Theme options
const THEME_OPTIONS = [
  { value: 'indoor', label: '🏢 Indoor', description: 'Interior setting' },
  { value: 'outdoor', label: '🌳 Outdoor', description: 'Natural outdoor scene' },
  { value: 'dark', label: '🌙 Dark Mode', description: 'Dark, moody atmosphere' },
  { value: 'bright', label: '☀️ Bright', description: 'Well-lit, cheerful' },
  { value: 'minimal', label: '⚪ Minimal', description: 'Clean, simple background' },
  { value: 'busy', label: '🎨 Busy', description: 'Detailed, active scene' }
];

function AIMakeoverModal({ user, imageData, onClose, onSave }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Regenerate options
  const [regeneratePrompt, setRegeneratePrompt] = useState('');
  const [selectedTheme, setSelectedTheme] = useState('');
  const [currentPromptIndex, setCurrentPromptIndex] = useState(0);
  
  const [processedImage, setProcessedImage] = useState(imageData);
  const [originalImage] = useState(imageData);
  const [showRegenerateSection, setShowRegenerateSection] = useState(false);

  const effectiveIndustry = user.current_industry || user.industry;

  const regenerateImage = async () => {
    setLoading(true);
    setError('');

    try {
      // Build prompt with theme and custom text
      let fullPrompt = `Recreate this image for ${effectiveIndustry} industry`;
      
      if (selectedTheme) {
        const theme = THEME_OPTIONS.find(t => t.value === selectedTheme);
        fullPrompt += ` with ${theme.description.toLowerCase()}`;
      }
      
      if (regeneratePrompt.trim()) {
        fullPrompt += `. ${regeneratePrompt}`;
      }
      
      fullPrompt += '. IMPORTANT: Do NOT place any text, logos, or important elements in the bottom right corner - keep that area clear for watermark.';

      const response = await axios.post(`${API}/generate/image/fast`, {
        user_id: user.id,
        industry: effectiveIndustry,
        prompt: fullPrompt
      });

      setProcessedImage(`data:${response.data.mime_type};base64,${response.data.image_base64}`);
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to regenerate image');
    } finally {
      setLoading(false);
    }
  };

  const usePromptSuggestion = (prompt) => {
    setRegeneratePrompt(prompt);
  };

  const nextPrompt = () => {
    setCurrentPromptIndex((prev) => (prev + 1) % PROMPT_SUGGESTIONS.length);
  };

  const prevPrompt = () => {
    setCurrentPromptIndex((prev) => (prev - 1 + PROMPT_SUGGESTIONS.length) % PROMPT_SUGGESTIONS.length);
  };

  const resetAll = () => {
    setProcessedImage(originalImage);
    setRegeneratePrompt('');
    setSelectedTheme('');
  };

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 border-2 border-orange-500/30 rounded-2xl max-w-6xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold gradient-text">🎨 AI Makeover</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-3xl">×</button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Image Preview */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-3">Preview</h4>
            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
              <img 
                src={processedImage} 
                alt="Preview" 
                className="w-full rounded-lg"
              />
            </div>
            <button
              onClick={resetAll}
              className="mt-3 w-full px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition text-sm"
            >
              🔄 Reset to Original
            </button>
          </div>

          {/* Options */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-white mb-3">🔄 Regenerate Image</h4>

            {/* Error Display */}
            {error && (
              <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-2 rounded-lg text-sm">
                {error}
              </div>
            )}

            {/* Regenerate Image Section */}
            <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
              <div className="space-y-3">
                  {/* Theme Selection */}
                  <div>
                    <label className="block text-xs text-gray-400 mb-2">Image Theme</label>
                    <div className="grid grid-cols-2 gap-2">
                      {THEME_OPTIONS.map((theme) => (
                        <button
                          key={theme.value}
                          onClick={() => setSelectedTheme(selectedTheme === theme.value ? '' : theme.value)}
                          className={`px-3 py-2 rounded-lg border transition text-sm text-left ${
                            selectedTheme === theme.value
                              ? 'border-teal-500 bg-teal-500/20 text-teal-400'
                              : 'border-gray-700 bg-gray-800 text-gray-300 hover:border-teal-500/50'
                          }`}
                        >
                          <div className="font-medium">{theme.label}</div>
                          <div className="text-xs opacity-75">{theme.description}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Prompt Suggestions Slider */}
                  <div>
                    <label className="block text-xs text-gray-400 mb-2">Quick Prompts</label>
                    <div className="bg-gray-900 rounded-lg p-3 border border-gray-700">
                      <div className="flex items-center justify-between gap-2">
                        <button 
                          onClick={prevPrompt}
                          className="px-2 py-1 bg-gray-700 hover:bg-gray-600 rounded text-white text-sm"
                        >
                          ←
                        </button>
                        <button
                          onClick={() => usePromptSuggestion(PROMPT_SUGGESTIONS[currentPromptIndex])}
                          className="flex-1 px-3 py-2 bg-orange-500/20 border border-orange-500/50 hover:bg-orange-500/30 rounded text-orange-300 text-sm text-center transition"
                        >
                          {PROMPT_SUGGESTIONS[currentPromptIndex]}
                        </button>
                        <button 
                          onClick={nextPrompt}
                          className="px-2 py-1 bg-gray-700 hover:bg-gray-600 rounded text-white text-sm"
                        >
                          →
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 text-center mt-2">Click to use prompt or browse with arrows</p>
                    </div>
                  </div>

                  {/* Custom Prompt Input */}
                  <div>
                    <label className="block text-xs text-gray-400 mb-2">Custom Instructions (Optional)</label>
                    <textarea
                      value={regeneratePrompt}
                      onChange={(e) => setRegeneratePrompt(e.target.value)}
                      placeholder="e.g., Add more people, change background color..."
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-teal-500 outline-none text-sm"
                      rows="3"
                    />
                  </div>

                <button
                  onClick={regenerateImage}
                  disabled={loading}
                  className="w-full px-4 py-3 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-lg hover:from-teal-600 hover:to-cyan-600 transition disabled:opacity-50 font-medium"
                >
                  {loading ? '⏳ Regenerating...' : '🔄 Regenerate Image'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="mt-6 pt-6 border-t border-gray-700">
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-6 py-3 bg-gray-700 text-white rounded-lg font-medium hover:bg-gray-600 transition"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                onSave(processedImage);
                onClose();
              }}
              className="px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg font-medium hover:from-green-700 hover:to-emerald-700 transition btn-glow"
            >
              💾 Save & Continue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AIMakeoverModal;
